/**
 * Bunny URL resolution for "re-sign on read".
 *
 * Persisted Bunny signed URLs expire (≈1h for createLibraryAsset thumbnails,
 * 30d for avatars). Rather than store a URL that goes stale, callers store a
 * pointer — an `assetId` (asset-doc-backed: logos, episode thumbs, contract
 * PDFs) or a raw storage `path` (avatars) — and resolve a FRESH signed URL at
 * render time through here.
 *
 * Results are cached in-memory per (pointer, intent) until shortly before the
 * token expires, so repeated renders of the same image don't spam the callable.
 * On no pointer the caller's `fallback` (a legacy stored URL — Firebase URLs
 * are still valid) is returned as-is, which is what lets us skip a data
 * backfill for pre-Bunny docs.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { logger } from './loggerService';
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants';

// key -> { url, expiresSec, promise }
const cache = new Map();
// Re-mint this many seconds before the token actually expires, so we never
// hand out a URL that dies mid-use.
const SKEW_SEC = 60;

// The in-memory cache dies with the tab, so every visit to a logo-heavy page
// (e.g. /nexus/companies) re-paid one callable per image. Mirror it to
// localStorage, keyed the same way and validated against the token's OWN
// expiry, so a reload inside the token's lifetime renders with zero calls.
const STORE_KEY = 'bunnyUrlCache';
const STORE_MAX = 400;

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    for (const [key, entry] of Object.entries(entries || {})) {
      if (isFresh(entry)) cache.set(key, { url: entry.url, expiresSec: entry.expiresSec, promise: null });
    }
  } catch {
    // Unparseable or unavailable (private mode) — the memory cache still works.
  }
}

let persistTimer = null;

// Coalesce the writes: a logo-heavy page resolves dozens of URLs at once and
// each one would otherwise re-serialize the whole store.
function persist() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    writeStore();
  }, 250);
}

function writeStore() {
  try {
    const out = {};
    let count = 0;
    for (const [key, entry] of cache.entries()) {
      // An entry with no expiry can't be re-validated on a later load, so it
      // stays memory-only rather than risking a permanently stale URL.
      if (!entry.expiresSec || !isFresh(entry)) continue;
      out[key] = { url: entry.url, expiresSec: entry.expiresSec };
      if (++count >= STORE_MAX) break;
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(out));
  } catch {
    // Quota or private mode — caching is an optimization, never a hard failure.
  }
}

function cacheKey({ assetId, path, intent, width }) {
  return `${assetId ? `a:${assetId}` : `p:${path}`}|${intent}${width ? `|w:${width}` : ''}`;
}

function isFresh(entry) {
  if (!entry || !entry.url) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return !entry.expiresSec || entry.expiresSec - SKEW_SEC > nowSec;
}

loadPersisted();

/**
 * Resolve a fresh signed Bunny URL.
 *
 * @param {Object} opts
 * @param {string} [opts.assetId]    - asset-doc-backed (getBunnyAssetUrl)
 * @param {string} [opts.path]       - raw storage path (getBunnyPathUrl)
 * @param {string} [opts.intent]     - BUNNY_URL_INTENT.* (default THUMBNAIL)
 * @param {string} [opts.shareToken] - for unauthenticated public-share contexts
 * @param {string} [opts.fallback]   - returned as-is when no assetId/path
 * @param {boolean} [opts.force]     - bypass + bust the cache (e.g. after a 403)
 * @returns {Promise<string|null>}
 */
/**
 * A stale Bunny signed URL is self-identifying (advanced-token auth: an HS256
 * token plus an expiry). Its pathname IS the storage path, so an expired URL
 * can be re-signed instead of rendered. Firebase/other legacy URLs don't match
 * and are left alone.
 */
function bunnyPathFromFallback(fallbackUrl) {
  if (!fallbackUrl || !/[?&]token=HS256/.test(fallbackUrl) || !/[?&]expires=/.test(fallbackUrl)) return null;
  try {
    return decodeURIComponent(new URL(fallbackUrl).pathname).replace(/^\/+/, '');
  } catch {
    return null;
  }
}

export async function refreshBunnyUrl({
  assetId,
  path: rawPath,
  intent = BUNNY_URL_INTENT.THUMBNAIL,
  shareToken,
  fallback = null,
  width = null,
  force = false,
} = {}) {
  // Every caller gets the stale-URL recovery, not just the ones that bothered
  // to derive a path themselves.
  const path = rawPath || bunnyPathFromFallback(fallback);
  if (!assetId && !path) return fallback;
  const key = cacheKey({ assetId, path, intent, width });

  if (force) {
    cache.delete(key);
    persist();
  } else {
    const cached = cache.get(key);
    if (isFresh(cached)) return cached.url;
    if (cached?.promise) return cached.promise;
  }

  const fns = getFunctions();

  const signByAsset = async () => {
    const fn = httpsCallable(fns, 'getBunnyAssetUrl');
    const payload = { assetId, intent };
    if (shareToken) payload.shareToken = shareToken;
    if (width) payload.width = width;
    const { data } = await fn(payload);
    return { url: data?.url || null, expiresSec: data?.expires || null };
  };

  const signByPath = async () => {
    const fn = httpsCallable(fns, 'getBunnyPathUrl');
    const { data } = await fn({ path, intent });
    return { url: data?.url || null, expiresSec: data?.expires || null };
  };

  const promise = (async () => {
    let url = null;
    let expiresSec = null;

    if (assetId) {
      try {
        ({ url, expiresSec } = await signByAsset());
      } catch (err) {
        // A DANGLING assetId ("Asset not found") is a data problem, not a
        // reason to render a broken image: the caller's `path` (or the storage
        // path recovered from a stale signed `fallback`) can still be signed.
        // Without this, the fallback we return is an EXPIRED signed URL, which
        // 403s — a permanently broken logo until someone fixes the doc.
        logger.warn('[bunnyUrlService] Asset lookup failed, trying path', {
          assetId,
          error: err.message,
        });
      }
    }

    if (!url && path) {
      try {
        ({ url, expiresSec } = await signByPath());
      } catch (err) {
        logger.warn('[bunnyUrlService] Failed to resolve Bunny URL', {
          assetId: assetId || null,
          path,
          error: err.message,
        });
      }
    }

    if (url) {
      cache.set(key, { url, expiresSec, promise: null });
      persist();
      return url;
    }
    cache.delete(key);
    return fallback;
  })();

  cache.set(key, { ...(cache.get(key) || {}), promise });
  return promise;
}

/**
 * Fetch a signed Bunny image and return it as a `data:` URI.
 *
 * html2canvas (behind html2pdf) loads images through its own cloned-document
 * pipeline, where a signed Bunny URL intermittently fails to load — the
 * "Error loading image" it logs then produces a PDF with a missing logo. A
 * data URI takes the network out of the capture entirely.
 *
 * Returns null on failure so callers degrade to a logo-less export rather
 * than losing the whole PDF.
 */
export async function bunnyUrlToDataUri(url) {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const res = await fetch(url);
    // A hotlink/expiry rejection resolves with an HTML error body — inlining
    // that would render a broken image instead of no image.
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    logger.warn('[bunnyUrlService] Failed to inline image as a data URI', {
      error: err.message,
    });
    return null;
  }
}
